import { Avatar, Badge, Checkbox, Stack, Table, Text } from "@mantine/core";
import React, { memo, useCallback, useMemo } from "react";
import { IconUser } from "@tabler/icons-react";
import {
  OrganizerStatusBadge,
  StatusBadge,
} from "@components/common/StatusBadge";
import type { ClientRowProps } from "./types";
import type { ClientTag } from "@/types/client";
import { getConsistentColorForUser, getInitials } from "./utils/assigneeUtils";
import ClientRowTags from "./ClientRowTags";

/**
 * Memoized client row component with proper comparison to prevent unnecessary re-renders
 */
const ClientRow = memo(
  ({ client, handleClientClick, isSelected, onSelect }: ClientRowProps) => {
    // Use clientId or userId depending on what the backend returns
    const clientIdentifier = client.clientId || client.userId;

    // Memoize expensive computations
    const clientName = useMemo(
      () =>
        client.firstName && client.lastName
          ? `${client.firstName} ${client.lastName}`
          : "Unnamed Client",
      [client.firstName, client.lastName]
    );

    const handleCheckboxChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onSelect(clientIdentifier, e.currentTarget.checked);
      },
      [clientIdentifier, onSelect]
    );

    const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleRowClick = useCallback(
      (e: React.MouseEvent) => {
        handleClientClick(e, clientIdentifier);
      },
      [clientIdentifier, handleClientClick]
    );

    const handleStatusClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        // Select this client and don't navigate
        onSelect(clientIdentifier, true);
      },
      [clientIdentifier, onSelect]
    );

    const handleAssigneeClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        // Select this client and don't navigate
        onSelect(clientIdentifier, true);
      },
      [clientIdentifier, onSelect]
    );

    // Get client status for StatusBadge
    const clientStatus = client?.status || client?.taxInfo?.status || "New";

    // Get organizer status
    const organizerStatus = client?.taxInfo?.organizerStatus;

    // Get assignee display name and avatar
    const assigneeName = client.assignee
      ? `${client.assignee.firstName || ""} ${client.assignee.lastName || ""}`.trim() ||
        "Unassigned"
      : "Unassigned";
    const assigneeInitials = client.assignee
      ? getInitials(client.assignee.firstName, client.assignee.lastName)
      : "U";
    // Use consistent colors based on userId - same as AssigneeFilterChips
    const assigneeColor = getConsistentColorForUser(client.assignee?.userId);

    return (
      <Table.Tr
        data-testid="client-list-item"
        onClick={handleRowClick}
        className={`client-row-clickable ${client.isArchived ? "client-row-archived" : ""}`}
      >
        <Table.Td className="checkbox-cell">
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={handleCheckboxClick}
            aria-label={`Select ${clientName}`}
          />
        </Table.Td>
        <Table.Td className="client-name-cell">
          <Stack gap={0}>
            <Text
              fw={600}
              size="sm"
              c="#00477A"
              onClick={handleRowClick}
              className="client-name"
              data-testid="client-name"
            >
              {clientName}
            </Text>
            <ClientRowTags tags={client.tags} />
          </Stack>
        </Table.Td>
        <Table.Td className="client-status-cell">
          <StatusBadge
            status={clientStatus}
            onClick={handleStatusClick}
            className="clickable-status-badge"
          />
        </Table.Td>
        <Table.Td className="organizer-status-cell">
          <OrganizerStatusBadge organizerStatus={organizerStatus} />
        </Table.Td>
        <Table.Td className="assignee-cell">
          <Badge
            onClick={handleAssigneeClick}
            className="clickable-assignee-badge"
            variant="outline"
            size="lg"
            radius="xl"
            leftSection={
              <Avatar
                size={24}
                radius="xl"
                color={assigneeColor}
                styles={{
                  root: {
                    backgroundColor: assigneeColor,
                    color: "white",
                  },
                  placeholder: {
                    color: "white",
                    fontSize: "11px",
                    fontWeight: 600,
                  },
                }}
              >
                {client.assignee ? (
                  <span style={{ fontSize: "11px", fontWeight: 600 }}>
                    {assigneeInitials}
                  </span>
                ) : (
                  <IconUser size={14} />
                )}
              </Avatar>
            }
            styles={{
              root: {
                backgroundColor: "white",
                color: "#333",
                borderColor: assigneeColor,
                borderWidth: "2px",
                fontWeight: 500,
                textTransform: "none",
                paddingLeft: "10px",
                paddingRight: "14px",
                paddingTop: "8px",
                paddingBottom: "8px",
                fontSize: "14px",
                height: "auto",
                cursor: "pointer",
              },
              section: {
                marginRight: "8px",
              },
            }}
          >
            {assigneeName}
          </Badge>
        </Table.Td>
      </Table.Tr>
    );
  },
  // Custom comparison function for better memoization
  (prevProps, nextProps) => {
    const prevClientId = prevProps.client.clientId || prevProps.client.userId;
    const nextClientId = nextProps.client.clientId || nextProps.client.userId;

    // Compare tags arrays
    const prevTags = prevProps.client.tags || [];
    const nextTags = nextProps.client.tags || [];
    const tagsEqual =
      prevTags.length === nextTags.length &&
      prevTags.every(
        (tag: ClientTag, index: number) => tag.id === nextTags[index]?.id
      );

    return (
      prevClientId === nextClientId &&
      prevProps.client.taxInfo?.status === nextProps.client.taxInfo?.status &&
      prevProps.client.status === nextProps.client.status &&
      prevProps.client.taxInfo?.organizerStatus ===
        nextProps.client.taxInfo?.organizerStatus &&
      prevProps.client.taxInfo?.organizerCompleteDate ===
        nextProps.client.taxInfo?.organizerCompleteDate &&
      prevProps.client.organizerUpdated === nextProps.client.organizerUpdated &&
      prevProps.client.assignee?.userId === nextProps.client.assignee?.userId &&
      prevProps.client.isArchived === nextProps.client.isArchived &&
      tagsEqual &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.handleClientClick === nextProps.handleClientClick &&
      prevProps.onSelect === nextProps.onSelect
    );
  }
);

ClientRow.displayName = "ClientRow";

export default ClientRow;
